import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import InstagramSection from '../components/InstagramSection';
import TestimonialsSection from '../components/TestimonialsSection';
import { getPhotos } from '../config/api';
import LoadingHero from '../components/LoadingHero';

const HERO_FALLBACK = '#2d2a27';
// Default hero image that loads immediately
const DEFAULT_HERO_IMAGE = 'https://res.cloudinary.com/dtyznx2i3/image/upload/v1775469142/photography/xslrzcv3bw43jr7lwzru.jpg';

export default function Home() {
  const [bgIdx, setBgIdx] = useState(0);
  const [heroBgs, setHeroBgs] = useState([DEFAULT_HERO_IMAGE]);
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const navigate = useNavigate();
  const showLoading = heroBgs.length === 1 && heroBgs[0] === DEFAULT_HERO_IMAGE;

  useEffect(() => {
    if (heroBgs.length > 1) {
      const t = setInterval(() => setBgIdx(i => (i + 1) % heroBgs.length), 4000);
      return () => clearInterval(t);
    }
  }, [heroBgs.length]);

  useEffect(() => {
    const loadHeroImages = async () => {
      try {
        // Fetch photos from homepage category
        const photosRes = await getPhotos(null, 0, 10, { show_on_homepage: true });
        const photos = Array.isArray(photosRes.data?.data) ? photosRes.data.data : [];

        if (photos.length === 0) {
          // No homepage category set or category has no photos
          // Keep showing LoadingHero with default static image
          setHeroBgs([DEFAULT_HERO_IMAGE]);
          return;
        }

        // Extract photo URLs directly (no category deduplication needed)
        const urls = photos.map(p => p.url).filter(url => url);

        if (urls.length > 0) {
          // Replace with loaded photos
          setHeroBgs(urls);

          // Preload remaining images in background
          if (urls.length > 1) {
            urls.slice(1).forEach(url => {
              const img = new Image();
              img.src = url;
            });
          }
        }
      } catch (e) {
        console.error('Failed to load hero images:', e);
        // Keep showing LoadingHero on error
      }
    };
    loadHeroImages();
  }, []);

  // Warmup detection: show message if loading takes > 3 seconds
  useEffect(() => {
    if (showLoading) {
      const warmupTimer = setTimeout(() => {
        if (showLoading) {
          setIsWarmingUp(true);
        }
      }, 3000);

      return () => clearTimeout(warmupTimer);
    } else {
      setIsWarmingUp(false);
    }
  }, [showLoading]);

  return (
    <div>
      <Navbar />

      <section className="hero-section" style={{ height: '100vh', backgroundColor: HERO_FALLBACK, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {/* LoadingHero - always present, fades in/out */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: showLoading ? 1 : 0,
          transition: 'opacity 1.2s ease-in-out',
          pointerEvents: showLoading ? 'auto' : 'none',
          zIndex: 2
        }}>
          <LoadingHero />
          {isWarmingUp && (
            <div style={{
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              padding: '0 24px',
              maxWidth: '500px'
            }}>
              <p style={{
                fontFamily: 'var(--font-body)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '14px',
                lineHeight: '1.6',
                marginTop: '20px'
              }}>
                Loading your photos... The server is waking up, this may take up to 30 seconds on first visit.
              </p>
            </div>
          )}
        </div>

        {/* Carousel backgrounds - fade in when photos load */}
        {!showLoading && heroBgs.map((url, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: bgIdx === i ? 1 : 0,
              transition: 'opacity 1.2s ease-in-out',
              zIndex: 0
            }}
          />
        ))}

        {/* Overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', zIndex: 1 }} />

        {/* Hero Content - always present, fades in/out */}
        <div className="hero-content" style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          padding: '0 24px',
          opacity: showLoading ? 0 : 1,
          transition: 'opacity 1.2s ease-in-out',
          pointerEvents: showLoading ? 'none' : 'auto'
        }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--hero-font-size)', fontWeight: 300, color: '#fff', letterSpacing: '0.06em', marginBottom: 12 }}>
            Bhagyashree Dhavalshankh
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', color: 'rgba(255,255,255,0.75)', fontSize: 14, letterSpacing: '0.04em', marginBottom: 28 }}>
            Celebrating life's real moments — un-posed, raw, imperfectly beautiful
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/gallery')} style={{ background: 'var(--color-accent)', color: '#fff', border: 'none', padding: '12px 32px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              View Gallery
            </button>
            <button onClick={() => navigate('/contact')} style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.65)', padding: '12px 32px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              Book a Shoot
            </button>
          </div>
          {heroBgs.length > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 28 }}>
              {heroBgs.map((_, i) => (
                <div key={i} onClick={() => setBgIdx(i)} style={{ width: 7, height: 7, borderRadius: '50%', cursor: 'pointer', background: bgIdx === i ? '#fff' : 'rgba(255,255,255,0.35)', transition: 'background 0.3s' }} />
              ))}
            </div>
          )}
        </div>
      </section>

      <TestimonialsSection />

      <InstagramSection />

      <Footer />
    </div>
  );
}